import { ORG_PERMISSION } from "../services/org/org.const"
import { getRefId } from "./utils"
import intersection from "lodash/intersection"

/**
 * Whether this org person may manage a project from the org side, without
 * being on its team: an admin, or a manager who is not a basic company admin
 * and whose accessible departments / offices (when limited) overlap the
 * project's.
 *
 * Mirrors the API: mission.rules.canWriteMissionAllowedFields admits org
 * admins and non-basic org managers, and the mission routes' department /
 * office restrictions scope the managers.
 *
 * @param meInOrg {Object} The org.people entry for the current person
 * @param mission {Object} The mission document
 * @return {Boolean}
 */
export function isOrgManagerForMission(meInOrg, mission) {
    if (!meInOrg) return false

    if (meInOrg.permission === ORG_PERMISSION.ADMIN) return true

    if (meInOrg.permission !== ORG_PERMISSION.MANAGER || meInOrg.isBasicCompanyAdmin) return false

    if (meInOrg.accessibleDepartments?.length && mission?.departmentTags?.length) {
        if (!intersection(meInOrg.accessibleDepartments, mission.departmentTags).length) {
            return false
        }
    }

    if (meInOrg.accessibleOffices?.length && mission?.offices?.length) {
        if (!intersection(meInOrg.accessibleOffices, mission.offices).length) {
            return false
        }
    }

    return true
}

/**
 * Organisation permission rules that anything may ask, including modules that
 * must not pull the org API (and therefore firebase) in with them - mAX's
 * workspace tools are the reason this lives here rather than in comp/OrgUtils,
 * which re-exports it so every existing caller is unchanged.
 */

/**
 * Whether this person may create a project in this organisation.
 *
 * An org can restrict creation to admins and the people explicitly granted it;
 * otherwise every manager (limited or not) can create one too.
 *
 * @param person {Object} app.state.person
 * @param org {Object} The org document
 * @return {Boolean}
 */
export function canCreateOrgProject(person, org) {
    if (!person || !org?._id) return false

    const op = (org.people || []).find((p) => {
        const a = getRefId(p)
        const b = getRefId(person)
        return a === b
    })

    if (!op) return false

    if (org.projectCreationRestricted) {
        return op.permission === ORG_PERMISSION.ADMIN || op.canCreateProjects
    } else if (
        op.permission === ORG_PERMISSION.ADMIN ||
        op.permission === ORG_PERMISSION.MANAGER ||
        op.permission === ORG_PERMISSION.MANAGER_LIMITED ||
        op.canCreateProjects
    ) {
        return true
    } else {
        return false
    }
}

/**
 * Whether this person may create an opportunity (a proposal) in this
 * organisation.
 *
 * Proposals are an add-on: the org has to be licensed and have them switched
 * on. Managers and admins can always create one; an org project manager only
 * when the org lets them.
 *
 * Lives here rather than in comp/OrgUtils for the reason above (it is the same
 * question mAX's workspace tools have to ask); OrgUtils re-exports it, so every
 * existing caller is unchanged.
 *
 * @param person {Object} app.state.person
 * @param org {Object} The org document
 * @return {Boolean}
 */
export function canCreateOrgProposal(person, org) {
    if (!person || !org?._id || !org.isLicensedActive || !org.hasProposals) return false

    const op = (org.people || []).find((p) => {
        const a = getRefId(p)
        const b = getRefId(person)
        return a === b
    })

    if (!op) return false

    if (op.permission === ORG_PERMISSION.ADMIN || op.permission === ORG_PERMISSION.MANAGER) {
        return true
    }

    return op.permission === ORG_PERMISSION.MANAGER_LIMITED && !!org.canProjectManagersCreateProposals
}

/**
 * Whether this person may create a client record in this organisation.
 *
 * The same rule the client picker applies before it offers its "new client"
 * button (SelectAddClient.jsx): admins and managers always, an org project
 * manager only when the org lets them keep order codes.
 *
 * @param person {Object} app.state.person
 * @param org {Object} The org document
 * @return {Boolean}
 */
export function canCreateOrgClient(person, org) {
    if (!person || !org?._id) return false

    const op = (org.people || []).find((p) => {
        const a = getRefId(p)
        const b = getRefId(person)
        return a === b
    })

    if (!op) return false

    if (op.permission === ORG_PERMISSION.ADMIN || op.permission === ORG_PERMISSION.MANAGER) {
        return true
    }

    return op.permission === ORG_PERMISSION.MANAGER_LIMITED && !!org.canProjectManagersCreateOrderCodes
}

/**
 * Whether this person may create and edit the organisation's client invoices.
 *
 * The API's rule (clientInvoice.rules.js, canWriteClientInvoiceAllowedFields):
 * admins, and managers granted invoice management. The invoices screen also
 * needs a licensed organisation that has not switched invoicing off.
 *
 * @param person {Object} app.state.person
 * @param org {Object} The org document
 * @return {Boolean}
 */
export function canManageOrgInvoices(person, org) {
    if (!person || !org?._id || !org.isLicensedActive || org.invoicingDisabled) return false

    const op = (org.people || []).find((p) => getRefId(p) === getRefId(person))

    if (!op) return false

    return (
        op.permission === ORG_PERMISSION.ADMIN || (op.permission === ORG_PERMISSION.MANAGER && !!op.canManageInvoices)
    )
}

/**
 * Access levels as offered by the People filters. `basic-admin` is a senior manager
 * (permission 1) with isBasicCompanyAdmin set - see matchesAccessLevel.
 */
export const ORG_ACCESS_LEVELS = [
    {
        label: "missionX administrators",
        id: 2,
    },
    {
        label: "Senior management",
        id: 1,
    },
    {
        label: "Company administration",
        id: "basic-admin",
    },
    {
        label: "Project managers",
        id: 4,
    },
    {
        label: "Employees",
        id: 0,
    },
    {
        label: "Observers",
        id: 3,
    },
]

/**
 * Whether an org person sits at one of the ORG_ACCESS_LEVELS ids. No level matches everyone.
 */
export function matchesAccessLevel(person, level) {
    if (level === null || level === undefined) return true
    if (level === "basic-admin") return Boolean(person?.permission === 1 && person?.isBasicCompanyAdmin)
    return person?.permission === level
}

/**
 * Display text + tooltip for an org permission. Pass "1B" for a basic company admin.
 */
export const getPermissionText = (val) => {
    let text = ""
    let tip = ""

    switch (val) {
        case 0:
            text = "Employee"
            tip = "Can only access projects they are on and admin items like timesheets and leave tracker"
            break
        case 4:
            text = "Project manager"
            tip =
                "Can create projects for the organization. Can only access projects and PM tools in the Mission. Vission. Board. Also, if you allow it in the Org Config panel they may  be able to add new clients and PO numbers."

            break
        case 1:
            text = "Senior management"
            tip = ""
            break
        case "1B":
            text = "Basic company admin"
            tip = ""
            break

        case 2:
            text = "Administrator 💥"
            tip = ""
            break

        case 3:
            text = "Observer"
            tip = ""
            break

        default:
    }

    return { text, tip }
}
